import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { config } from '../config';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  BookOpen,
  BarChart3,
  Send,
  RotateCcw,
  FileText,
  Layers,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  FolderOpen,
  Book,
  ArrowLeft
} from 'lucide-react';
import RenderMathOrText from './RenderMathOrText';

const apiUrl = config.apiUrl;

const revisionStages = [
  24 * 60 * 60,    // 24 hours
  24 * 60 * 60,    // 24 hours
  3 * 24 * 60 * 60, // 3 days
  7 * 24 * 60 * 60, // 7 days
  15 * 24 * 60 * 60, // 15 days
  30 * 24 * 60 * 60  // 30 days
];

const Home = () => {
  const [subjects, setSubjects] = useState([]);
  const [modules, setModules] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [selectedSubject, setSelectedSubject] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [selectedLesson, setSelectedLesson] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [selectedOptions, setSelectedOptions] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState({});
  const [timePerQuestion] = useState(30);
  const [timeLeft, setTimeLeft] = useState(timePerQuestion);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [view, setView] = useState('subjects');
  const [expandedSubject, setExpandedSubject] = useState(null);
  const [notes, setNotes] = useState([]);
  const [flashcards, setFlashcards] = useState([]);
  const [currentCardIndex, setCurrentCardIndex] = useState(0);
  const [showBack, setShowBack] = useState(false);
  const [revisionTimers, setRevisionTimers] = useState({});
  const [subjectRevisionCounts, setSubjectRevisionCounts] = useState({});
  const [moduleRevisionCounts, setModuleRevisionCounts] = useState({});

   const formatTime = (totalSeconds = 0) => {
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    const pad = (n) => String(n).padStart(2, '0');
    return `${pad(h)}:${pad(m)}:${pad(s)}`;
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = localStorage.getItem('token');
    if (!token) {
      setError('আপনি লগইন করেননি। দয়া করে লগইন করুন।');
      setLoading(false);
      return;
    }
    
    const fetchSubjects = async () => {
      try {
        const response = await axios.get(`${apiUrl}/subjects`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setSubjects(response.data);
        
        // Fetch revision counts for all subjects
        const revisionCounts = {};
        for (const subject of response.data) {
          try {
            const modulesResponse = await axios.get(`${apiUrl}/subjects/${subject.id}/modules`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            let totalRevisionCount = 0;
            for (const module of modulesResponse.data.data || []) {
              try {
                const lessonsResponse = await axios.get(`${apiUrl}/modules/${module.id}/lessons-with-revision`, {
                  headers: { 'Authorization': `Bearer ${token}` }
                });
                
                const lessons = lessonsResponse.data.data || lessonsResponse.data || [];
                
                lessons.forEach(lesson => {
                  if (lesson.next_revision_at) {
                    const nextRevisionTime = new Date(lesson.next_revision_at).getTime();
                    const currentTime = Date.now();
                    if (nextRevisionTime <= currentTime) {
                      totalRevisionCount++;
                    }
                  } 
                  
                  // else {
                  //   // যদি next_revision_at না থাকে
                  //   totalRevisionCount++;
                  // }
                });
              } catch (error) {
                console.error(`Error fetching lessons for module ${module.id}:`, error);
              }
            }
            
            revisionCounts[subject.id] = totalRevisionCount;
          } catch (error) {
            console.error(`Error fetching modules for subject ${subject.id}:`, error);
            revisionCounts[subject.id] = 0;
          }
        }
        
        setSubjectRevisionCounts(revisionCounts);
      } catch (err) {
        setError('বিষয়গুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
      } finally {
        setLoading(false);
      }
    };
    
    fetchSubjects();
  }, []);

  // Update revision timers every second
  useEffect(() => {
    const interval = setInterval(() => {
      setRevisionTimers(prevTimers => {
        const updatedTimers = {...prevTimers};
        let changed = false;
        
        Object.keys(updatedTimers).forEach(lessonId => {
          if (updatedTimers[lessonId].remainingTime > 0) {
            updatedTimers[lessonId].remainingTime -= 1;
            changed = true;
          }
        });
        
        return changed ? updatedTimers : prevTimers;
      });
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  const getRevisionTimer = (lessonId) => {
    return revisionTimers[lessonId] || null;
  };

  const setRevisionTimer = async (lessonId) => {
    try {
      const token = localStorage.getItem('token');
      const currentTimer = getRevisionTimer(lessonId);
      let nextRevisionNumber = currentTimer ? currentTimer.stageIndex + 1 : 0;
      
      if (nextRevisionNumber >= revisionStages.length) {
        nextRevisionNumber = revisionStages.length - 1; // Keep at the last stage
      }
      
      const response = await axios.patch(
        `${apiUrl}/lessons/${lessonId}/update-revision`,
        { revision_number: nextRevisionNumber },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      
      const currentTime = Math.floor(Date.now() / 1000);
      const newTimer = {
        lessonId,
        lastRevised: currentTime,
        stageTime: revisionStages[nextRevisionNumber],
        remainingTime: revisionStages[nextRevisionNumber],
        stageIndex: nextRevisionNumber
      };
      
      setRevisionTimers(prev => ({...prev, [lessonId]: newTimer}));
      
    } catch (err) {
      console.error('রিভিশন আপডেট করতে সমস্যা হয়েছে:', err);
    }
  };

  const getRevisionStatus = (lessonId) => {
    const timer = getRevisionTimer(lessonId);
    if (!timer) return { needsRevision: true, message: 'অধ্যয়ন শুরু করুন', timeLeft: 0 };
    
    const needsRevision = timer.remainingTime <= 0;
    const days = Math.floor(timer.remainingTime / (24 * 60 * 60));
    const hours = Math.floor((timer.remainingTime % (24 * 60 * 60)) / 3600);
    const minutes = Math.floor((timer.remainingTime % 3600) / 60);
    const seconds = timer.remainingTime % 60;
    
    let message = needsRevision ? 'দ্রুত রিভিশন দিন' : 
                 days > 0 ? `${days}d ${hours}h ${minutes}m ${seconds}s` : 
                 hours > 0 ? `${hours}h ${minutes}m ${seconds}s` :
                 `${minutes}মিনিট ${seconds}সেকেন্ড`;
    
    return { needsRevision, message, timeLeft: timer.remainingTime };
  };

  // Fetch modules for selected subject with revision status
  const fetchModules = async (subjectId) => {
    setContentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/subjects/${subjectId}/modules`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      // Fetch lessons for each module to calculate revision status
      const modulesWithRevision = await Promise.all(
        (response.data.data || []).map(async (module) => {
          try {
            const lessonsResponse = await axios.get(`${apiUrl}/modules/${module.id}/lessons-with-revision`, {
              headers: { 'Authorization': `Bearer ${token}` }
            });
            
            const lessons = lessonsResponse.data.data || lessonsResponse.data || [];
            let needsRevisionCount = 0;
            
            lessons.forEach(lesson => {
              if (lesson.next_revision_at) {
                const nextRevisionTime = new Date(lesson.next_revision_at).getTime();
                const currentTime = Date.now();
                if (nextRevisionTime <= currentTime) {
                  needsRevisionCount++;
                }
              }
            });
            
            return {
              ...module,
              needsRevisionCount
            };
          } catch (error) {
            console.error(`Error fetching lessons for module ${module.id}:`, error);
            return {
              ...module,
              needsRevisionCount: 0
            };
          }
        })
      );
      
      setModules(modulesWithRevision);
      
      // Update module revision counts
      const moduleCounts = {};
      modulesWithRevision.forEach(module => {
        moduleCounts[module.id] = module.needsRevisionCount;
      });
      setModuleRevisionCounts(moduleCounts);
    } catch (err) {
      setError('মডিউলগুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch lessons for selected module with revision data
  const fetchLessons = async (moduleId) => {
    setContentLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${apiUrl}/modules/${moduleId}/lessons-with-revision`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const lessonsData = response.data.data || response.data || [];
      setLessons(lessonsData);
      
      // Initialize revision timers for lessons
      const revisionData = {};
      lessonsData.forEach(lesson => {
        if (lesson.next_revision_at) {
          const nextRevisionTime = new Date(lesson.next_revision_at).getTime() / 1000;
          const currentTime = Math.floor(Date.now() / 1000);
          const remainingTime = Math.max(0, nextRevisionTime - currentTime);
          
          revisionData[lesson.id] = {
            lessonId: lesson.id,
            lastRevised: lesson.last_revised_at ? new Date(lesson.last_revised_at).getTime() / 1000 : currentTime,
            stageTime: revisionStages[lesson.revision_number] || revisionStages[0],
            remainingTime: remainingTime,
            stageIndex: lesson.revision_number || 0
          };
        } else {
          // If no revision data exists, mark as needing revision
          revisionData[lesson.id] = {
            lessonId: lesson.id,
            lastRevised: Math.floor(Date.now() / 1000),
            stageTime: revisionStages[0],
            remainingTime: 0,
            stageIndex: 0
          };
        }
      });
      
      setRevisionTimers(revisionData);
    } catch (err) {
      setError('লেসনগুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch questions for selected lesson
  const fetchQuestionsByLesson = async (lessonId) => {
    setContentLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/questions-by-lesson/${lessonId}`);
      setQuestions(response.data.data || []);
      setTimeLeft((response.data.data.length * 36) || timePerQuestion);
    } catch (err) {
      console.error('Error details:', err.response);
      setError('প্রশ্নগুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch notes for selected lesson
  const fetchNotesByLesson = async (lessonId) => {
    setContentLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/notes/lesson/${lessonId}`);
      setNotes(response.data);
    } catch (err) {
      setError('নোটগুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setContentLoading(false);
    }
  };

  // Fetch flashcards for selected lesson
  const fetchFlashcardsByLesson = async (lessonId) => {
    setContentLoading(true);
    try {
      const response = await axios.get(`${apiUrl}/flashes/lesson/${lessonId}`);
      const sortedFlashcards = response.data.sort((a, b) => b.id - a.id);
      setFlashcards(sortedFlashcards);
      setCurrentCardIndex(0);
      setShowBack(false);
    } catch (err) {
      setError('ফ্ল্যাশকার্ডগুলি লোড করতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setContentLoading(false);
    }
  };

  const handleSubjectClick = async (subject) => {
    setExpandedSubject(expandedSubject === subject.id ? null : subject.id);
    setSelectedSubject(subject);
    
    if (expandedSubject !== subject.id) {
      await fetchModules(subject.id);
    }
  };

  const handleModuleClick = async (module) => {
    setSelectedModule(module);
    await fetchLessons(module.id);
    setView('lessons');
  };

  const handleContentTypeSelect = async (type, lesson) => {
    setSelectedLesson(lesson);
    
    if (type === 'mcq') {
      await fetchQuestionsByLesson(lesson.id);
      setView('exam');
    } else if (type === 'notes') {
      await fetchNotesByLesson(lesson.id);
      setView('notes');
    } else if (type === 'flashcards') {
      await fetchFlashcardsByLesson(lesson.id);
      setView('flashcards');
    }
  };

  // Timer effect for exam
  useEffect(() => {
    if (timeLeft > 0 && !submitted && !submitLoading && view === 'exam') {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0 && !submitted && !submitLoading && view === 'exam') {
      handleAutoSubmit();
    }
  }, [timeLeft, submitted, submitLoading, view]);

  const handleOptionSelect = (questionId, optionId) => {
    if (!submitted && !submitLoading) {
      setSelectedOptions(prev => ({...prev, [questionId]: optionId}));
    }
  };

  const handleSubmit = async () => {
    setSubmitLoading(true);
    setSubmitted(true);
    try {
      const token = localStorage.getItem('token');
      const submissionResults = {};
      
      for (const question of questions) {
        const selectedOptionId = selectedOptions[question?.id] || null;
        if (!selectedOptionId) {
          submissionResults[question?.id] = {
            isCorrect: false,
            selectedOptionId: null,
            correctOptionId: question?.answer?.option_id ?? null,
            notAttempted: true
          };
          continue;
        }
        
        try {
          const response = await axios.post(
            `${apiUrl}/submit-answer`, 
            { question_id: question?.id, selected_option_id: selectedOptionId },
            { headers: { 'Authorization': `Bearer ${token}` } }
          );
          
          submissionResults[question?.id] = {
            isCorrect: response.data.is_correct,
            selectedOptionId: selectedOptionId,
            correctOptionId: response.data.correct_option_id ?? null
          };
        } catch (err) {
          submissionResults[question?.id] = {
            isCorrect: false,
            selectedOptionId: selectedOptionId,
            correctOptionId: null,
            error: err.response?.data?.message || 'উত্তর জমা দিতে সমস্যা হয়েছে'
          };
        }
      }
      
      setResults(submissionResults);
      setView('results');
    } catch (err) {
      setError('উত্তর জমা দিতে সমস্যা হয়েছে: ' + (err.response?.data?.message || err.message));
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleAutoSubmit = () => {
    if (!submitted && !submitLoading) handleSubmit();
  };

  const resetExam = () => {
    setSelectedOptions({});
    setSubmitted(false);
    setResults({});
    setCurrentQuestionIndex(0);
    setTimeLeft(timePerQuestion);
    setError(null);
    setView('exam');
  };

  const goBackToSubjects = () => {
    setSelectedSubject(null);
    setSelectedModule(null);
    setSelectedLesson(null);
    setQuestions([]);
    setNotes([]);
    setFlashcards([]);
    setSelectedOptions({});
    setSubmitted(false);
    setResults({});
    setCurrentQuestionIndex(0);
    setError(null);
    setView('subjects');
    setExpandedSubject(null);
    setCurrentCardIndex(0);
    setShowBack(false);
  };

  const goBackToModules = () => {
    setSelectedModule(null);
    setView('subjects');
  };

  const goBackToLessons = () => {
    setSelectedLesson(null);
    setView('lessons');
  };

  const calculateScore = () => {
    const totalQuestions = questions.length;
    const correctAnswers = Object.values(results).filter(r => r?.isCorrect).length;
    return {
      total: totalQuestions,
      correct: correctAnswers,
      score: totalQuestions > 0 ? Math.round((correctAnswers / totalQuestions) * 100) : 0
    };
  };

  const getOptionStyle = (questionId, optionId) => {
    if (!submitted) {
      return selectedOptions[questionId] === optionId
        ? 'border-blue-500 bg-blue-50'
        : 'border-gray-300 hover:border-gray-400';
    }
    
    const result = results[questionId];
    if (!result || result.error) return 'border-gray-300';
    
    if (result.correctOptionId && optionId === result.correctOptionId) {
      return 'border-green-500 bg-green-50';
    } else if (optionId === result.selectedOptionId && !result.isCorrect) {
      return 'border-red-500 bg-red-50';
    } else if (optionId === result.selectedOptionId && result.isCorrect) {
      return 'border-green-500 bg-green-50';
    }
    
    return 'border-gray-300';
  };

  const getOptionIcon = (questionId, optionId) => {
    if (!submitted) return null;
    const result = results[questionId];
    if (!result || result.error) return null;
    
    if (result.correctOptionId && optionId === result.correctOptionId) {
      return <CheckCircle className="h-5 w-5 text-green-500 ml-2" />;
    } else if (optionId === result.selectedOptionId && !result.isCorrect) {
      return <XCircle className="h-5 w-5 text-red-500 ml-2" />;
    } else if (optionId === result.selectedOptionId && result.isCorrect) {
      return <CheckCircle className="h-5 w-5 text-green-500 ml-2" />;
    }
    
    return null;
  };

  // Calculate revision counts for subjects and modules
  // Calculate revision counts for subjects and modules
  const calculateRevisionCounts = () => {
    const subjectRevisionCounts = {};
    const moduleRevisionCounts = {};
    
    subjects.forEach(subject => {
      subjectRevisionCounts[subject.id] = 0;
    });
    
    modules.forEach(module => {
      // যদি module এ needsRevisionCount property থাকে
      if (module.needsRevisionCount !== undefined) {
        moduleRevisionCounts[module.id] = module.needsRevisionCount;
        if (selectedSubject) {
          subjectRevisionCounts[selectedSubject.id] = (subjectRevisionCounts[selectedSubject.id] || 0) + module.needsRevisionCount;
        }
      } else {
        moduleRevisionCounts[module.id] = 0;
      }
    });
    
    return { subjectRevisionCounts, moduleRevisionCounts };
  };

  // Subjects View
if (view === 'subjects') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-800 flex items-center justify-center">
                <BookOpen className="h-8 w-8 mr-2 text-blue-600" />
                বিষয়ভিত্তিক শিক্ষা
              </h1>
              <p className="text-gray-600 mt-2">একটি বিষয় নির্বাচন করে শুরু করুন</p>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : error ? (
              <div className="text-center py-8">
                <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">{error}</p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  আবার চেষ্টা করুন
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                <a
                  href="/ielts"
                  className="block rounded-lg border border-indigo-200 bg-indigo-50 p-4 transition-colors hover:bg-indigo-100"
                >
                  <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-xl font-semibold text-indigo-900">IELTS Practice</h3>
                      <p className="text-sm text-indigo-700">Listening, Reading and Writing practice</p>
                    </div>
                    <span className="font-semibold text-indigo-800">Open IELTS</span>
                  </div>
                </a>
                {subjects.map((subject) => (
                  <div key={subject.id} className="border border-gray-200 rounded-lg overflow-hidden">
                    <div
                      onClick={() => handleSubjectClick(subject)}
                      className="bg-blue-50 p-4 cursor-pointer hover:bg-blue-100 transition-colors flex justify-between items-center"
                    >
                      <div className="flex items-center">
                        <h3 className="text-xl font-semibold text-blue-800">{subject.name}</h3>
                        {subjectRevisionCounts[subject.id] > 0 && (
                          <span className="ml-3 bg-red-100 text-red-800 text-sm font-medium px-2 py-1 rounded-full">
                            {subjectRevisionCounts[subject.id]}টি লেসনে রিভিশন প্রয়োজন
                          </span>
                        )}
                      </div>
                      <div className="flex items-center">
                        {subjectRevisionCounts[subject.id] > 0 && expandedSubject !== subject.id && (
                          <span className="mr-2 bg-red-500 text-white text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                            {subjectRevisionCounts[subject.id]}
                          </span>
                        )}
                        {expandedSubject === subject.id ? (
                          <ChevronUp className="h-5 w-5 text-blue-600" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-blue-600" />
                        )}
                      </div>
                    </div>
                    
                    {expandedSubject === subject.id && (
                      <div className="p-4 bg-white">
                        {contentLoading ? (
                          <div className="flex justify-center py-4">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                          </div>
                        ) : modules.length === 0 ? (
                          <div className="text-center text-gray-500 py-4">
                            <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-2" />
                            <p>এই বিষয়ের জন্য কোনো মডিউল পাওয়া যায়নি</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            <h4 className="font-semibold text-gray-700">মডিউলসমূহ:</h4>
                            {modules.map((module) => (
                              <div
                                key={module.id}
                                className="border border-gray-200 rounded-lg p-3 cursor-pointer hover:bg-gray-50 transition-colors"
                                onClick={() => handleModuleClick(module)}
                              >
                                <div className="flex justify-between items-center">
                                  <div className="flex items-center">
                                    <h5 className="font-medium text-gray-800">{module.name}</h5>
                                    {moduleRevisionCounts[module.id] > 0 && (
                                      <span className="ml-3 bg-red-100 text-red-800 text-xs font-medium px-2 py-1 rounded-full">
                                        {moduleRevisionCounts[module.id]}টি লেসনে রিভিশন প্রয়োজন
                                      </span>
                                    )}
                                  </div>
                                  <span className="text-sm text-gray-500">
                                    {module.lessons?.length || 0}টি লেসন
                                  </span>
                                </div>
                                {module.description && (
                                  <p className="text-sm text-gray-600 mt-1">{module.description}</p>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  // Lessons View
  if (view === 'lessons' && selectedModule) {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button
                  onClick={() => {
                    setSelectedModule(null);
                    setView('subjects');
                  }}
                  className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{selectedModule.name}</h1>
                  <p className="text-gray-600">{selectedSubject?.name}</p>
                  {moduleRevisionCounts[selectedModule.id] > 0 && (
                    <p className="text-red-600 text-sm mt-1">
                      {moduleRevisionCounts[selectedModule.id]}টি লেসনে রিভিশন প্রয়োজন
                    </p>
                  )}
                </div>
              </div>
            </div>

            {contentLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : lessons.length === 0 ? (
              <div className="text-center text-gray-500 py-8">
                <Book className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">এই মডিউলের জন্য কোনো লেসন পাওয়া যায়নি</p>
              </div>
            ) : (
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-800">লেসনসমূহ:</h3>
                {lessons.map((lesson) => {
                  const revisionStatus = getRevisionStatus(lesson.id);
                  
                  return (
                    <div key={lesson.id} className="border border-gray-200 rounded-lg p-4">
                      <div className="flex justify-between items-center mb-2">
                        <h4 className="font-semibold text-gray-800">{lesson.title}</h4>
                        {/* <span className={`text-sm font-medium px-2 py-1 rounded ${
                          revisionStatus.needsRevision ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                        }`}>
                          {revisionStatus.needsRevision ? 'রিভিশন প্রয়োজন' : `পরবর্তী রিভিশন: ${revisionStatus.message}`}
                        </span> */}
                      </div>
                      
                      {lesson.content && (
                        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                          {lesson.content.replace(/<[^>]*>/g, '').substring(0, 150)}...
                        </p>
                      )}
                      
                      <div className="flex justify-between items-center mb-3">
                        <div>
                          <p className={revisionStatus.needsRevision ? 'text-red-600 text-sm font-medium' : 'text-green-600 text-sm'}>
                            {revisionStatus.needsRevision ? lesson.revision_number > 0? 'রিভিশন প্রয়োজন': 'অধ্যয়ন প্রয়োজন' : `পরবর্তী রিভিশন: ${revisionStatus.message}`}
                          </p>
                        </div>
                        <button
                          onClick={(e) => { 
                            e.stopPropagation();
                            setRevisionTimer(lesson.id);
                          }}
                          className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center text-sm"
                        >
                          <RefreshCw className="h-4 w-4 mr-1" /> 
                          {lesson.next_revision_at === null ? 'পাঠ শেষ করেছি' : 'রিভিশন সম্পন্ন করেছি'} 
                        </button>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div
                          onClick={() => handleContentTypeSelect('mcq', lesson)}
                          className="bg-green-50 border-2 border-green-200 rounded-lg p-3 cursor-pointer hover:bg-green-100 transition-colors text-center"
                        >
                          <BarChart3 className="h-8 w-8 text-green-600 mx-auto mb-1" />
                          <h5 className="font-semibold text-green-800 text-sm">MCQ পরীক্ষা</h5>
                          <p className="text-green-600 text-xs">প্রশ্নপত্র সমাধান করুন</p>
                        </div>
                        
                        <div
                          onClick={() => handleContentTypeSelect('notes', lesson)}
                          className="bg-yellow-50 border-2 border-yellow-200 rounded-lg p-3 cursor-pointer hover:bg-yellow-100 transition-colors text-center"
                        >
                          <FileText className="h-8 w-8 text-yellow-600 mx-auto mb-1" />
                          <h5 className="font-semibold text-yellow-800 text-sm">নোটস</h5>
                          <p className="text-yellow-600 text-xs">স্টাডি নোটস দেখুন</p>
                        </div>
                        
                        <div
                          onClick={() => handleContentTypeSelect('flashcards', lesson)}
                          className="bg-purple-50 border-2 border-purple-200 rounded-lg p-3 cursor-pointer hover:bg-purple-100 transition-colors text-center"
                        >
                          <Layers className="h-8 w-8 text-purple-600 mx-auto mb-1" />
                          <h5 className="font-semibold text-purple-800 text-sm">ফ্ল্যাশকার্ড</h5>
                          <p className="text-purple-600 text-xs">মেমোরি কার্ডস দেখুন</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Notes View
  if (view === 'notes') {
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button
                  onClick={goBackToLessons}
                  className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{selectedLesson?.title}</h1>
                  <p className="text-gray-600">{selectedModule?.name} - {selectedSubject?.name}</p>
                </div>
              </div>
            </div>

            {contentLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : notes.length === 0 ? (
              <div className="text-center py-8">
                <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">এই লেসনের জন্য কোনো নোট পাওয়া যায়নি</p>
              </div>
            ) : (
              <div className="space-y-6">
                {notes.map((note) => (
                  <div key={note.id} className="border border-gray-200 rounded-lg p-6">
                    <h3 className="text-xl font-semibold text-gray-800 mb-3">{note.title}</h3>
                    <div className="prose max-w-none">
                        <div className="whitespace-pre-wrap">
                          <RenderMathOrText html={note.content} />
                        </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Flashcards View
  if (view === 'flashcards') {
    const currentCard = flashcards[currentCardIndex];

    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <button
                  onClick={goBackToLessons}
                  className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-800">{selectedLesson?.title}</h1>
                  <p className="text-gray-600">{selectedModule?.name} - {selectedSubject?.name}</p>
                </div>
              </div>
              <div className="text-gray-600">
                কার্ড {currentCardIndex + 1} / {flashcards.length}
              </div>
            </div>

            {contentLoading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              </div>
            ) : flashcards.length === 0 ? (
              <div className="text-center py-8">
                <Layers className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">এই লেসনের জন্য কোনো ফ্ল্যাশকার্ড পাওয়া যায়নি</p>
              </div>
            ) : (
              <div className="text-center">
                <div
                  onClick={() => setShowBack(!showBack)}
                  className="bg-blue-50 border-2 border-blue-200 rounded-lg p-8 cursor-pointer hover:bg-blue-100 transition-colors min-h-[300px] flex items-center justify-center mx-auto max-w-md"
                >
                  <div className="text-xl font-semibold">
                    <RenderMathOrText html={showBack ? currentCard.back : currentCard.front} />
                  </div>
                </div>
                
                <div className="flex justify-center space-x-4 mt-6">
                  <button
                    onClick={() => {
                      setCurrentCardIndex(prev => Math.max(0, prev - 1));
                      setShowBack(false);
                    }}
                    disabled={currentCardIndex === 0}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    পূর্ববর্তী
                  </button>
                  
                  <button
                    onClick={() => setShowBack(!showBack)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    {showBack ? 'সামনে দেখুন' : 'পিছনে দেখুন'}
                  </button>
                  
                  <button
                    onClick={() => {
                      setCurrentCardIndex(prev => Math.min(flashcards.length - 1, prev + 1));
                      setShowBack(false);
                    }}
                    disabled={currentCardIndex === flashcards.length - 1}
                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 transition-colors"
                  >
                    পরবর্তী
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Exam View
  if (view === 'exam') {
    if (contentLoading) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      );
    }

    if (questions.length === 0) {
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center">
          <div className="text-center">
            <BookOpen className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600 mb-4">এই লেসনের জন্য কোনো প্রশ্ন পাওয়া যায়নি</p>
            <button
              onClick={goBackToLessons}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              লেসনে ফিরে যান
            </button>
          </div>
        </div>
      );
    }

    const currentQuestion = questions[currentQuestionIndex];
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          {/* Header */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4">
              <div>
                <div className="flex items-center mb-2">
                  <button
                    onClick={goBackToLessons}
                    className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                  >
                    <ArrowLeft className="h-5 w-5 text-gray-600" />
                  </button>
                  <div>
                    <h1 className="text-xl font-bold text-gray-800">{selectedLesson?.title}</h1>
                    <p className="text-gray-600 text-sm">{selectedModule?.name} - {selectedSubject?.name}</p>
                  </div>
                </div>
                <p className="text-gray-600">
                  প্রশ্ন {currentQuestionIndex + 1} / {questions.length}
                </p>
              </div>
              
              <div className="flex items-center space-x-4 mt-4 md:mt-0">
                <div className="flex items-center text-orange-600">
                  <Clock className="h-5 w-5 mr-2" />
                  <span className="font-semibold">{formatTime(timeLeft)}</span>
                </div>
              </div>
            </div>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 mb-4">
              <div
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{
                  width: `${((currentQuestionIndex + 1) / questions.length) * 100}%`
                }}
              ></div>
            </div>

            {/* Question navigation */}
            <div className="grid grid-cols-5 md:grid-cols-10 gap-2 mb-4">
              {questions.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentQuestionIndex(index)}
                  className={`p-2 rounded-lg text-sm font-medium transition-colors ${
                    currentQuestionIndex === index
                      ? 'bg-blue-600 text-white'
                      : selectedOptions[questions[index]?.id]
                      ? 'bg-blue-100 text-blue-600'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {index + 1}
                </button>
              ))}
            </div>
          </div>

          {/* Question Card */}
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="px-3 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-medium">
                {currentQuestion?.difficulty === 'easy' ? 'সহজ' : 
                 currentQuestion?.difficulty === 'medium' ? 'মধ্যম' : 'কঠিন'}
              </span>
              <span className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm font-medium">
                মান: {currentQuestion?.mark ?? 0}
              </span>
            </div>

            <div className="mb-6">
              <div className="text-lg font-semibold text-gray-800 mb-4">
                <RenderMathOrText html={currentQuestion?.question_text || 'প্রশ্ন পাওয়া যায়নি'} />
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {currentQuestion?.options?.map((option, index) => (
                <div
                  key={option?.id}
                  onClick={() => handleOptionSelect(currentQuestion?.id, option?.id)}
                  className={`p-4 border rounded-lg cursor-pointer transition-colors flex items-center justify-between ${getOptionStyle(
                    currentQuestion?.id,
                    option?.id
                  )} ${submitLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center mr-3 font-semibold">
                      {String.fromCharCode(65 + index)}
                    </div>
                    <span className="text-gray-700">{option?.option_text || '-'}</span>
                  </div>
                  {getOptionIcon(currentQuestion?.id, option?.id)}
                </div>
              ))}
            </div>
          </div>

          {/* Navigation & Submit */}
          <div className="flex justify-between space-x-4">
            <button
              onClick={() => setCurrentQuestionIndex(prev => Math.max(0, prev - 1))}
              disabled={currentQuestionIndex === 0 || submitLoading}
              className="px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              পূর্ববর্তী
            </button>

            {currentQuestionIndex < questions.length - 1 ? (
              <button
                onClick={() => setCurrentQuestionIndex(prev => Math.min(questions.length - 1, prev + 1))}
                disabled={submitLoading}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                পরবর্তী
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitLoading}
                className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
              >
                <Send className="h-4 w-4 mr-2" />
                উত্তর জমা দিন
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Results View
  if (view === 'results') {
    const score = calculateScore();
    
    return (
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="text-center mb-8">
              <div className="flex items-center justify-center mb-4">
                <button
                  onClick={goBackToLessons}
                  className="mr-3 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <ArrowLeft className="h-5 w-5 text-gray-600" />
                </button>
                <h1 className="text-2xl font-bold text-gray-800">
                  {selectedLesson?.title} - পরীক্ষার ফলাফল
                </h1>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-sm text-green-600">সঠিক উত্তর</p>
                  <p className="text-2xl font-bold text-green-800">{score.correct}</p>
                </div>
                <div className="bg-red-50 p-4 rounded-lg border border-red-200">
                  <p className="text-sm text-red-600">ভুল উত্তর</p>
                  <p className="text-2xl font-bold text-red-800">{score.total - score.correct}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-600">মোট স্কোর</p>
                  <p className="text-2xl font-bold text-blue-800">{score.score}%</p>
                </div>
              </div>
            </div>

            {/* Results Details */}
            <div className="space-y-4">
              {questions.map((question, index) => {
                const result = results[question?.id];
                const selectedOption = question?.options?.find(opt => opt?.id === result?.selectedOptionId);
                const correctOption = question?.options?.find(opt => opt?.id === result?.correctOptionId);
                const selectedIndex = question?.options?.findIndex(opt => opt?.id === result?.selectedOptionId);
                const correctIndex = question?.options?.findIndex(opt => opt?.id === result?.correctOptionId);

                return (
                  <div
                    key={question?.id || index}
                    className={`p-4 rounded-lg border ${
                      result?.isCorrect
                        ? 'border-green-200 bg-green-50'
                        : result?.notAttempted
                        ? 'border-gray-200 bg-gray-50'
                        : result?.error
                        ? 'border-yellow-200 bg-yellow-50'
                        : 'border-red-200 bg-red-50'
                    }`}
                  >
                    <div className="mb-3">
                      <span className="font-medium">প্রশ্ন {index + 1}:</span>{" "}
                      <RenderMathOrText html={question?.question_text || "-"} />
                    </div>

                    {selectedOption && (
                      <p className="text-sm mb-1">
                        <span className="font-medium">আপনার উত্তর:</span> 
                        {String.fromCharCode(65 + selectedIndex)}.{" "}
                        <RenderMathOrText html={selectedOption?.option_text || "-"} />
                      </p>
                    )}

                    {result?.notAttempted && (
                      <p className="text-sm text-gray-600">আপনি এই প্রশ্নের উত্তর দেননি</p>
                    )}

                    {result?.error && (
                      <p className="text-sm text-yellow-600">{result.error}</p>
                    )}

                    {correctOption && !result?.isCorrect && (
                      <p className="text-sm text-green-600 font-medium">
                        <span className="font-medium">সঠিক উত্তর:</span> 
                        {String.fromCharCode(65 + correctIndex)}.{" "}
                        <RenderMathOrText html={correctOption?.option_text || "-"} />
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center space-x-4 mt-8">
              <button
                onClick={resetExam}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                আবার চেষ্টা করুন
              </button>
              
              <button
                onClick={goBackToLessons}
                className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
              >
                <Book className="h-4 w-4 mr-2" />
                লেসনে ফিরে যান
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default Home;
